import React, { useRef, useEffect } from 'react';
import BaseModal from '../common/BaseModal';

const DiscountCodeModal = ({
  show,
  discount,
  discountVerificationCode,
  discountCustomerCard,
  discountInfluencerName,
  discountCodeError,
  onVerificationCodeChange,
  onCustomerCardChange,
  onInfluencerNameChange,
  onSubmit,
  onCancel
}) => {
  const codeInputRef = useRef(null);
  const cardInputRef = useRef(null);
  const influencerInputRef = useRef(null);

  const requiresCode = discount?.wymaga_kodu === 1 || discount?.wymaga_kodu === true;
  const requiresCard = discount?.wymaga_karty_klienta === 1 || discount?.wymaga_karty_klienta === true;
  const requiresInfluencer = discount?.wymaga_influencera === 1 || discount?.wymaga_influencera === true;

  // Ustaw focus na odpowiedni input po otwarciu modalu
  useEffect(() => {
    if (show) {
      // Krótkie opóźnienie żeby modal się wyrenderował
      const timer = setTimeout(() => {
        if (requiresCode && codeInputRef.current) {
          codeInputRef.current.focus();
        } else if (requiresCard && cardInputRef.current) {
          cardInputRef.current.focus();
        } else if (requiresInfluencer && influencerInputRef.current) {
          influencerInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show, requiresCode, requiresCard, requiresInfluencer]);

  const isFormValid = () => {
    const codeValid = !requiresCode || discountVerificationCode.length === 12;
    const cardValid = !requiresCard || discountCustomerCard.trim();
    const influencerValid = !requiresInfluencer || discountInfluencerName.trim();
    return codeValid && cardValid && influencerValid;
  };

  const footer = (
    <>
      <button
        onClick={onCancel}
        style={{
          padding: "6px 12px",
          border: "1px solid var(--border)",
          backgroundColor: "var(--card-bg)",
          color: "var(--text-muted)",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        Anuluj
      </button>
      <button
        onClick={onSubmit}
        disabled={!isFormValid()}
        style={{
          padding: "6px 12px",
          border: isFormValid() ? "1px solid var(--success)" : "1px solid var(--border)",
          backgroundColor: "var(--card-bg)",
          color: isFormValid() ? "var(--success)" : "var(--text-muted)",
          cursor: isFormValid() ? "pointer" : "not-allowed",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        <i className="fas fa-check" style={{ marginRight: "0.5rem" }}></i>
        Zastosuj rabat
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={show}
      onClose={onCancel}
      title={discount?.nazwa || 'Rabat'}
      headerIcon={<i className="fas fa-tag" style={{ fontSize: "1rem" }}></i>}
      headerStyle={{ backgroundColor: "var(--purple)" }}
      size="sm"
      zIndex={1060}
      footer={footer}
    >
      <div style={{ marginBottom: "1rem", padding: "0.5rem", backgroundColor: "var(--bg)", borderRadius: "4px", fontSize: "0.875rem" }}>
        Wartość rabatu: <strong style={{ color: "var(--success)" }}>
          {discount?.typ_rabatu === 'procentowy' ? `${discount?.wartosc}%` : `${discount?.wartosc} zł`}
        </strong>
      </div>

      {/* Pole kodu weryfikacyjnego */}
      {requiresCode && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: "500" }}>
            Kod weryfikacyjny (12 cyfr): *
          </label>
          <input
            ref={codeInputRef}
            type="text"
            maxLength="12"
            value={discountVerificationCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 12);
              onVerificationCodeChange(value);
            }}
            placeholder="Wpisz lub zeskanuj kod"
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid var(--border)",
              borderRadius: "6px",
              fontSize: "1.25rem",
              fontFamily: "monospace",
              letterSpacing: "2px",
              textAlign: "center",
            }}
          />
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
            Wprowadzono: {discountVerificationCode.length}/12 cyfr
          </div>
        </div>
      )}

      {/* Pole numeru karty klienta */}
      {requiresCard && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: "500" }}>
            Numer karty klienta: *
          </label>
          <input
            ref={cardInputRef}
            type="text"
            value={discountCustomerCard}
            onChange={(e) => onCustomerCardChange(e.target.value)}
            placeholder="Wpisz numer karty"
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid var(--border)",
              borderRadius: "6px",
              fontSize: "1rem",
            }}
          />
        </div>
      )}

      {/* Pole nazwy influencera */}
      {requiresInfluencer && (
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", fontWeight: "500" }}>
            Nazwa influencera / polecającego: *
          </label>
          <input
            ref={influencerInputRef}
            type="text"
            value={discountInfluencerName}
            onChange={(e) => onInfluencerNameChange(e.target.value)}
            placeholder="Wpisz nazwę influencera"
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid var(--purple-light)",
              borderRadius: "6px",
              fontSize: "1rem",
              backgroundColor: "var(--purple-light)",
            }}
          />
        </div>
      )}

      {/* Błąd walidacji */}
      {discountCodeError && (
        <div style={{
          padding: "0.5rem 0.75rem",
          backgroundColor: "var(--danger-light)",
          borderRadius: "4px",
          color: "var(--danger)",
          fontSize: "0.875rem",
        }}>
          <i className="fas fa-exclamation-triangle" style={{ marginRight: "0.5rem" }}></i>
          {discountCodeError}
        </div>
      )}
    </BaseModal>
  );
};

export default React.memo(DiscountCodeModal);
